import { AxiosError, AxiosResponse } from 'axios';
import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { DeepPartial } from 'redux';

// @ts-ignore
import { resetSelectedTableCells } from 'services/selectedTableCells/actions';
import {
    FERTILIZER_EVENT_UPDATE_MODAL,
    FERTILIZER_EVENT_CREATE_MODAL,
    FERTILIZER_EVENT_DELETE_MODAL,
    FERTILIZER_EVENT_COPY_MODAL,
    FERTILIZER_EVENTS_COMPLETE_MODAL
} from 'scenes/Fertilizer/constants';

import { updateQueryString } from 'services/router/actions';
import { selectFertilizerSchemaId } from 'services/schemas/selectors';
import axiosPoll from 'utils/axiosPoll';
import { sortEventJobByJobTypes } from 'utils/events';
import {
    patchRequest,
    postRequest,
    putRequest,
    takeLatestWithCancel
} from 'utils/redux-saga';
import { formatDataForPostRequest } from 'utils/formatter/data-format';
import {
    failedCreateFertilizerEvent,
    failedDeleteFertilizerEvent,
    failedLoadFertilizerEvents,
    failedUpdateFertilizerEvent,
    failedCompleteFertilizerEvents,
    setFertilizerEvent,
    setFertilizerEvents,
    succeededCreateFertilizerEvent,
    succeededDeleteFertilizerEvent,
    succeededLoadFertilizerEvents,
    succeededUpdateFertilizerEvent,
    succeededCompleteFertilizerEvents
} from './actions';
import {
    FERTILIZER_EVENTS_LOAD_CANCELED,
    FERTILIZER_EVENTS_LOAD_REQUESTED,
    FERTILIZER_EVENT_CREATE_REQUESTED,
    FERTILIZER_EVENT_DELETE_REQUESTED,
    FERTILIZER_EVENT_UPDATE_REQUESTED,
    FERTILIZER_EVENTS_COMPLETE_REQUESTED
} from './constants';
import { selectLoadFertilizerEventsParams } from './params';
import { selectFertilizerEvents } from './selectors';
import {
    FertilizerEvent,
    FertilizerEventJob,
    RequestCreateFertilizerEventAction,
    RequestDeleteFertilizerEventAction,
    RequestUpdateFertilizerEventAction,
    RequestCompleteFertilizerEventsAction
} from './types';

const { REACT_APP_API_URL } = process.env;

export function* loadFertilizerEventsSucceeded(
    response: AxiosResponse
): SagaIterator {
    yield put(succeededLoadFertilizerEvents(keyBy(response.data, 'uuid')));
}

export function* loadFertilizerEventsFailed(error: AxiosError): SagaIterator {
    yield put(failedLoadFertilizerEvents(error));
}

export function* loadFertilizerEvents(): SagaIterator {
    const params = yield select(selectLoadFertilizerEventsParams);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Events`,
            params
        },
        succeeded: loadFertilizerEventsSucceeded,
        failed: loadFertilizerEventsFailed
    });
}

export function* watchFertilizerEventsLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        FERTILIZER_EVENTS_LOAD_REQUESTED,
        FERTILIZER_EVENTS_LOAD_CANCELED,
        loadFertilizerEvents
    );
}

export function* createFertilizerEvent(
    action: RequestCreateFertilizerEventAction
): SagaIterator {
    const { payload } = action;

    const fertilizerSchemaId = yield select(selectFertilizerSchemaId);

    try {
        const {
            entityId,
            startDate,
            endDate,
            supplier,
            fertilizerEntityIds,
            fertilizerProducts,
            applicationMethod,
            operatorPersonId,
            justification,
            technicalPersonName,
            comment
        } = payload;

        const values = {
            entityId,
            startDate,
            endDate,
            fertilizerEvent: formatDataForPostRequest({
                operatorPersonId,
                fertilizerEntityIds,
                supplier,
                justification,
                technicalPersonName,
                applicationMethod,
                fertilizerProducts,
                comment
            }),
            jobs: [
                {
                    jobType: 'INSPECTION'
                },
                {
                    jobType: 'TIMESHEET'
                }
            ],
            schemaId: fertilizerSchemaId
        };

        const response = yield call(postRequest, 'Events', values);

        if (response) {
            const uuid = get(response.data, 'uuid');

            yield all([
                put(setFertilizerEvent({ [uuid]: response.data })),
                put(succeededCreateFertilizerEvent(response)),
                put(
                    updateQueryString({
                        [FERTILIZER_EVENT_CREATE_MODAL]: undefined,
                        [FERTILIZER_EVENT_COPY_MODAL]: undefined,
                        fertilizerEventUuid: undefined
                    })
                )
            ]);
        }
    } catch (error) {
        yield put(failedCreateFertilizerEvent(error));
    }
}

export function* watchCreateFertilizerEventRequested(): SagaIterator {
    yield takeLatest(FERTILIZER_EVENT_CREATE_REQUESTED, createFertilizerEvent);
}

export function* updateFertilizerEvent(
    action: RequestUpdateFertilizerEventAction
): SagaIterator {
    const { payload } = action;

    try {
        const {
            startDate,
            endDate,
            supplier,
            fertilizerEntityIds,
            fertilizerProducts,
            applicationMethod,
            operatorPersonId,
            justification,
            technicalPersonName,
            comment,
            uuid
        } = payload;

        const fertilizerEvents = yield select(selectFertilizerEvents);
        const fertilizerEventId = get(fertilizerEvents, [uuid, 'id']);

        const values = {
            startDate,
            endDate,
            fertilizerEvent: {
                operatorPersonId,
                fertilizerEntityIds,
                supplier,
                justification,
                technicalPersonName,
                applicationMethod,
                fertilizerProducts,
                comment
            },
            jobs: [
                {
                    jobType: 'INSPECTION'
                },
                {
                    jobType: 'TIMESHEET'
                }
            ]
        };

        const response = yield call(
            patchRequest,
            'Events',
            values,
            fertilizerEventId
        );

        if (response) {
            const fertilizerEventUuid = get(response.data, 'uuid');
            const { data } = response;

            const jobs = sortEventJobByJobTypes(data.jobs);

            yield all([
                put(
                    setFertilizerEvent({
                        [fertilizerEventUuid]: { ...response.data, jobs }
                    })
                ),
                put(succeededUpdateFertilizerEvent(response)),
                put(
                    updateQueryString({
                        [FERTILIZER_EVENT_UPDATE_MODAL]: undefined,
                        fertilizerEventUuid: undefined
                    })
                )
            ]);
        }
    } catch (error) {
        yield put(failedUpdateFertilizerEvent(error));
    }
}

export function* watchUpdateFertilizerEventRequested(): SagaIterator {
    yield takeLatest(FERTILIZER_EVENT_UPDATE_REQUESTED, updateFertilizerEvent);
}

export function* deleteFertilizerEvent(
    action: RequestDeleteFertilizerEventAction
): SagaIterator {
    const uuids = action.payload;

    try {
        const response = yield call(putRequest, 'Events/bulk', {
            delete: uuids
        });

        const { delete: deletedFertilizerEvents, errors } = response.data;

        if (deletedFertilizerEvents && deletedFertilizerEvents.length) {
            const deletedUuids = deletedFertilizerEvents.map(
                (value: FertilizerEvent) => value.uuid
            );

            yield put(succeededDeleteFertilizerEvent(deletedUuids));
        }

        if (errors && errors.length) {
            yield put(failedDeleteFertilizerEvent(response));
        } else {
            yield all([
                put(
                    updateQueryString({
                        [FERTILIZER_EVENT_DELETE_MODAL]: undefined
                    })
                ),
                put(resetSelectedTableCells())
            ]);
        }
    } catch (error) {
        yield put(failedDeleteFertilizerEvent(error));
    }
}

export function* watchFertilizerEventDeleteRequested(): SagaIterator {
    yield takeLatest(FERTILIZER_EVENT_DELETE_REQUESTED, deleteFertilizerEvent);
}

export function* completeFertilizerEvents(
    action: RequestCompleteFertilizerEventsAction
): SagaIterator {
    const uuids = action.payload;

    try {
        const fertilizerEvents = yield select(selectFertilizerEvents);
        const completedAt = new Date();
        const updatePayload = uuids.reduce(
            (result: DeepPartial<FertilizerEvent>[], uuid) => {
                const fertilizerEventJobs: FertilizerEventJob[] = get(
                    fertilizerEvents,
                    [uuid, 'jobs'],
                    []
                );

                const jobs = fertilizerEventJobs.map(({ jobType }) => ({
                    jobType,
                    completedAt
                }));

                return [
                    ...result,
                    {
                        uuid,
                        jobs
                    }
                ];
            },
            []
        );

        const response = yield call(putRequest, 'Events/bulk', {
            update: updatePayload
        });

        const { update: updatedEvents, errors } = response.data;

        if (updatedEvents && updatedEvents.length) {
            yield all([
                put(succeededCompleteFertilizerEvents(response)),
                put(setFertilizerEvents(keyBy(updatedEvents, 'uuid')))
            ]);
        }

        if (errors && errors.length) {
            yield put(failedCompleteFertilizerEvents(response.data.errors));
        } else {
            yield all([
                put(
                    updateQueryString({
                        [FERTILIZER_EVENTS_COMPLETE_MODAL]: undefined
                    })
                ),
                put(resetSelectedTableCells())
            ]);
        }
    } catch (error) {
        yield put(failedCompleteFertilizerEvents(error));
    }
}

export function* watchFertilizerEventsCompleteRequested(): SagaIterator {
    yield takeLatest(
        FERTILIZER_EVENTS_COMPLETE_REQUESTED,
        completeFertilizerEvents
    );
}

export default function* fertilizerEventsSagas(): SagaIterator {
    yield all([
        fork(watchFertilizerEventsLoadRequested),
        fork(watchCreateFertilizerEventRequested),
        fork(watchUpdateFertilizerEventRequested),
        fork(watchFertilizerEventDeleteRequested),
        fork(watchFertilizerEventsCompleteRequested)
    ]);
}
